import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import { APIError, CancelledError } from "@/api/error";
import { env } from "@/env/env";

// Perform a GET request.
export const get = async <Res extends object, Req = any>(
  path: string,
  data?: Req,
  host?: string,
  config?: AxiosRequestConfig
) => {
  return axios
    .get(`${host || env().API_ORIGIN}/${path}`, {
      ...requestConfig(),
      ...config,
      params: { ...data },
    })
    .then((res) => res.data as Res)
    .catch(handleErrorResponse);
};

// Perform a GET or POST request for a file.
export const getFile = async (
  path: string,
  data?: any,
  usePostRequest?: boolean
) => {
  const requestPath = `${env().API_ORIGIN}/${path}`;
  const requestData = { ...data };
  const config: AxiosRequestConfig = {
    ...requestConfig(),
    responseType: "blob",
    params: usePostRequest ? undefined : requestData,
  };
  return (
    usePostRequest
      ? axios.post(
          requestPath,
          Object.keys(requestData).reduce((fd, key) => {
            fd.append(key, requestData[key]);
            return fd;
          }, new FormData()),
          config
        )
      : axios.get(requestPath, config)
  )
    .then((res) => {
      return res.data as Blob;
    })
    .catch(handleErrorResponse);
};

// Perform a POST request.
export const post = async <Res extends object, Req = any>(
  path: string,
  data?: Req,
  host?: string,
  config?: AxiosRequestConfig
) => {
  return axios
    .post(`${host || env().API_ORIGIN}/${path}`, requestData(data), {
      ...requestConfig(data),
      ...config,
    })
    .then((res) => res.data as Res)
    .catch(handleErrorResponse);
};

// Perform a PUT request.
export const put = async <Res extends object, Req = any>(
  path: string,
  data?: Req,
  host?: string
) => {
  return axios
    .put(
      `${host || env().API_ORIGIN}/${path}`,
      requestData(data),
      requestConfig(data)
    )
    .then((res) => res.data as Res)
    .catch(handleErrorResponse);
};

// Perform a PATCH request.
export const patch = async <Res extends object>(path: string, data: any) => {
  return axios
    .patch(
      `${env().API_ORIGIN}/${path}`,
      requestData(data),
      requestConfig(data)
    )
    .then((res) => res.data as Res)
    .catch(handleErrorResponse);
};

// Perform a DELETE request.
export const del = async (path: string) => {
  return axios
    .delete(`${env().API_ORIGIN}/${path}`, requestConfig())
    .then(() => undefined)
    .catch(handleErrorResponse);
};

// Create the Axios request data object.
export const requestData = (data?: any) => {
  if (typeof data === "string") {
    // Plain-text request data must be quoted.
    return `"${data}"`;
  }
  return data;
};

// Create the Axios request config object.
export const requestConfig = (data?: any) => {
  const config: AxiosRequestConfig = { withCredentials: true };
  if (typeof data !== "object") {
    // Plain-text request data needs the "text/plain" Content-Type.
    config["headers"] = { "Content-Type": "text/plain" };
  } else if (data.cancelToken) {
    config["cancelToken"] = data.cancelToken;
  }
  return config;
};

// Handle an API response error.
export const handleErrorResponse = (error: any) => {
  // In the ADC standalone app we do not auto-redirect on 401 — callers handle
  // unauthenticated state themselves (e.g. subscription/auth context fallbacks).
  throw createResponseError(error);
};

// Turn a 400/500 response into an Error object with a message.
export const createResponseError = (error: AxiosError<any>) => {
  if (axios.isCancel(error)) {
    return new CancelledError(error.message);
  }

  // we need to explicitly cast to AxiosError<any> because TypeScript infers the type of the error
  // as `never` in this branch
  const axiosError = error as AxiosError<any>;
  return new APIError(
    findUserErrorMessage(axiosError.response) ||
      findDeveloperErrorMessage(axiosError.response) ||
      axiosError.message,
    findAPIErrorReason(axiosError.response),
    axiosError.response?.status,
    // we need to cast to any because TypeScript infers the type of the expression as `{}`
    (axiosError.response?.data as any)?.details,
    (axiosError.response?.data as any)?.code
  );
};

// Find the api error code in a response.
const findAPIErrorReason = (response?: AxiosResponse) => {
  const detailsType = "type.googleapis.com/google.rpc.ErrorInfo";
  const detailsList = response?.data?.details || [];
  return detailsList.find((d: any) => d["@type"] === detailsType)?.reason;
};

// Find the user-friendly error message in a response.
const findUserErrorMessage = (response?: AxiosResponse) => {
  // NOTE: In some cases the API just returns a string on 403's (f ex "User account inactive")
  if (response?.status === 403 && typeof response?.data === "string")
    return response.data;
  const detailsType = "type.googleapis.com/google.rpc.LocalizedMessage";
  const detailsList = response?.data?.details || [];
  return detailsList.find((d: any) => d["@type"] === detailsType)?.message;
};

// Find the developer-friendly error message in a response.
const findDeveloperErrorMessage = (response?: AxiosResponse) => {
  return response?.data?.message;
};

/** Ignores errors caused by cancelled network requests, rethrows other errors */
export const ignoreCancelError = (error: unknown) => {
  if (axios.isCancel(error) || error instanceof CancelledError) return;
  throw error;
};

export type CRUD<Item> = {
  read: (id: string) => Promise<Item>;
  create: (item: Item) => Promise<Item>;
  update: (item: Item) => Promise<Item>;
  delete: (item: Item) => Promise<void>;
};

export const crud = {
  read: <Item extends object>(name: string) => get<Item, {}>(`v1/${name}`),
  create: <Item extends object>(item: Item) =>
    post<Item, Item>("v1/charts", item),
  update: <Item extends { name?: string }>(item: Item) =>
    patch<Item>(`v1/${item.name}`, item),
  delete: <Item extends { name?: string }>(item: Item) =>
    del(`v1/${item.name}`),
};

/**
 *
 * Encodes a given array as multiple query params of the form
 * key=value1&key=value2. The default axios paramSerializer
 * instead uses brackets which are not supported by the
 * backend.
 */
export const encodeArrayAsQueryParam = (key: string, array: string[]) =>
  array.map((value) => `${key}=${value}`).join("&");
